import React from "react";
import { ArrowRight, Calendar, MapPin, CalendarDays } from "lucide-react";
import { getShowSchedule } from "@/lib/shows";

export default function ShowsPage() {
  const { featured: NEXT_EVENT, upcoming: ALSO_UPCOMING } = getShowSchedule();

  return (
    <div data-testid="shows-page" className="max-w-7xl mx-auto px-4 sm:px-8 md:px-16 py-16">
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-500 mb-3">
        // Upcoming shows
      </div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
        Catch us <span className="text-red-600">IRL</span>.
      </h1>
      <p className="mt-4 text-slate-600 max-w-2xl">
        We&apos;re booth-vending across Alberta — sealed packs, singles &amp; slabs
        on hand at every show. Come by, trade with us, or grab a hit from the case.
      </p>

      {!NEXT_EVENT && (
        <div
          data-testid="shows-empty"
          className="mt-12 bg-white border border-slate-200 rounded-2xl p-10 text-center"
        >
          <CalendarDays className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h2 className="font-[Outfit] text-2xl font-black text-slate-900">
            No shows on the books right now.
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Check back soon — we&apos;ll announce the next date on our socials
            as soon as it&apos;s locked in.
          </p>
        </div>
      )}

      {NEXT_EVENT && (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 items-center bg-white border border-yellow-500/40 rounded-2xl overflow-hidden shadow-[0_8px_40px_rgba(250,204,21,0.08)]">
          {NEXT_EVENT.link ? (
            <a
              href={NEXT_EVENT.link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="shows-next-image"
              className="block bg-slate-50"
            >
              <img
                src={NEXT_EVENT.image_url}
                alt={NEXT_EVENT.name}
                loading="lazy"
                className="w-full lg:w-[400px] h-auto block hover:brightness-105 transition-all"
              />
            </a>
          ) : (
            <div data-testid="shows-next-image" className="block bg-slate-50">
              <img
                src={NEXT_EVENT.image_url}
                alt={NEXT_EVENT.name}
                loading="lazy"
                className="w-full lg:w-[400px] h-auto block"
              />
            </div>
          )}
          <div className="p-6 sm:p-8 lg:pr-10">
            <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-yellow-600 mb-2">
              Next event
            </div>
            <h2 className="font-[Outfit] text-2xl sm:text-3xl font-black leading-tight text-slate-900">
              {NEXT_EVENT.name}
            </h2>
            <div className="mt-4 space-y-2 text-sm text-slate-700">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-yellow-600" />
                <span className="font-mono">{NEXT_EVENT.dates}</span>
              </div>
              {NEXT_EVENT.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-yellow-600" />
                  <span>{NEXT_EVENT.location}</span>
                </div>
              )}
            </div>
            {NEXT_EVENT.link && (
              <a
                href={NEXT_EVENT.link}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="shows-next-cta"
                className="mt-6 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-2.5 rounded-md uppercase tracking-wide text-sm transition-colors"
              >
                Event details <ArrowRight className="w-4 h-4" />
              </a>
            )}
            <p className="mt-4 text-xs text-slate-600 leading-relaxed">
              {NEXT_EVENT.blurb}
            </p>
          </div>
        </div>
      )}

      {ALSO_UPCOMING.length > 0 && (
        <div className="mt-10">
          <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-500 mb-4">
            Also coming up
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ALSO_UPCOMING.map((ev) => (
              <a
                key={ev.slug || ev.name}
                href={ev.link || "#"}
                target={ev.link ? "_blank" : undefined}
                rel={ev.link ? "noopener noreferrer" : undefined}
                data-testid={`shows-event-${ev.slug}`}
                className="group bg-white border border-slate-200 hover:border-yellow-500/60 rounded-2xl overflow-hidden transition-all hover:-translate-y-1 hover:shadow-lg flex flex-col"
              >
                <div className="bg-slate-50 border-b border-slate-200 flex items-center justify-center overflow-hidden">
                  <img
                    src={ev.image_url}
                    alt={ev.name}
                    loading="lazy"
                    className="w-full h-auto block group-hover:brightness-95 transition-all"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-yellow-600 mb-1">
                    {ev.badge}
                  </div>
                  <h3 className="font-[Outfit] text-lg font-black text-slate-900 leading-tight">
                    {ev.name}
                  </h3>
                  <div className="mt-3 space-y-1.5 text-sm text-slate-700">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-yellow-600" />
                      <span className="font-mono">{ev.dates}</span>
                    </div>
                    {ev.location && (
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-yellow-600" />
                        <span>{ev.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-auto pt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-yellow-700 group-hover:text-yellow-600">
                    {ev.cta || "Event details"} <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
